import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { Link } from "react-router-dom";
import { FiEdit3, FiTrash2, FiEye, FiCopy } from "react-icons/fi";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const ProductTable = () => {
  const [rows, setRows] = useState([]);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  const fetchData = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/products`,
    );
    setRows(res.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleStatus = async (id, status) => {
    await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/api/products/status/${id}`,
      { status: status === "aktif" ? "pasif" : "aktif" },
    );
    fetchData();
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: getText("DESTROY_CONFIRM_TITLE"),
      message: getText("DESTROY_CONFIRM_MSG"),
      buttons: [
        {
          label: getText("YES_DELETE"),
          onClick: async () => {
            await axios.delete(
              `${process.env.REACT_APP_API_BASE_URL}/api/products/${id}`,
            );
            toast.success(getText("SUCCESS_DELETED"));
            fetchData();
          },
        },
        { label: getText("CANCEL") },
      ],
    });
  };

  const Switch = ({ checked, onChange }) => (
    <div className="form-check form-switch text-center">
      <input
        className="form-check-input"
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ cursor: "pointer", width: 45, height: 22 }}
      />
    </div>
  );

  const getTotalStock = (stockJson) => {
    if (!stockJson) return "Sınırsız";
    const values = Object.values(stockJson);
    if (!values.length) return "Sınırsız";

    let total = 0;
    for (const v of values) {
      if (v === null || v === "" || v === undefined) return "Sınırsız";
      total += Number(v);
    }
    return total;
  };
  const togglePopular = async (id) => {
    await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/api/products/popular/${id}`,
    );
    fetchData();
  };

  const formatCategories = (catArr) => {
    if (!Array.isArray(catArr)) return "-";
    if (catArr.length === 0) return "-";
    return (
      catArr.slice(0, 2).join(", ") +
      (catArr.length > 2 ? ` +${catArr.length - 2}` : "")
    );
  };

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: getText("TABLE_ID_LABEL"), size: 60 },
      { accessorKey: "title", header: getText("TABLE_TITLE_LABEL") },
      {
        accessorKey: "stock",
        header: getText("TABLE_STOCK_LABEL"),
        cell: ({ row }) => getTotalStock(row.original.stock),
      },

      { accessorKey: "barcode", header: getText("TABLE_BARCODE_LABEL") },
      {
        accessorKey: "add_to_cart_hit",
        header: getText("TABLE_CART_HIT_LABEL"),
        cell: ({ row }) => row.original.add_to_cart_hit || 0,
      },

      {
        accessorKey: "favorite_hit",
        header: getText("TABLE_FAVORITE_HIT_LABEL"),
        cell: ({ row }) => row.original.favorite_hit || 0,
      },
      {
        accessorKey: "is_popular",
        header: getText("TABLE_POPULAR_LABEL"),
        cell: ({ row }) => (
          <Switch
            checked={Boolean(row.original.is_popular)}
            onChange={() => togglePopular(row.original.id)}
          />
        ),
      },

      {
        accessorKey: "status",
        header: getText("TABLE_STATUS_LABEL"),
        cell: ({ row }) => (
          <Switch
            checked={row.original.status === "aktif"}
            onChange={() => toggleStatus(row.original.id, row.original.status)}
          />
        ),
      },

      {
        accessorKey: "actions",
        header: getText("TABLE_ACTIONS_LABEL"),
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Link
              to={`/products/view/${row.original.id}`}
              className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
              title={getText("VIEW")}
            >
              <FiEye />
            </Link>

            <Link
              to={`/products/edit/${row.original.id}`}
              className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
              title={getText("EDIT")}
            >
              <FiEdit3 />
            </Link>
            <Link
              to="/products/create"
              state={{
                copyProduct: row.original,
              }}
              className="avatar-text avatar-md text-warning bg-soft-warning border-soft-warning"
              title={getText("COPY")}
            >
              <FiCopy />
            </Link>
            <button
              className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
              onClick={() => handleDelete(row.original.id)}
              title={getText("DELETE")}
            >
              <FiTrash2 />
            </button>
          </div>
        ),
      },
    ],
    [translations],
  );

  return <Table data={rows} columns={columns} />;
};

export default ProductTable;
